import React, { useState } from 'react'
import { Group } from '@visx/group'
import { Bar } from '@visx/shape'
import { scaleBand, scaleLinear } from '@visx/scale'
import { animated, useSprings } from '@react-spring/web'
import { localPoint } from '@visx/event'
import { AxisBottom, AxisLeft } from '@visx/axis'
import { GridRows } from '@visx/grid'

// Generic data interface
interface DataItem {
  category: string
  value: number
}

interface ChartData {
  data: DataItem[]
  color: string
}

const defaultMargin = { top: 25, right: 40, bottom: 50, left: 50 }

export type ColumnChartProps = {
  width: number
  height: number
  margin?: typeof defaultMargin
  chartData: ChartData
  backgroundColor?: string
  tooltipUnit?: string
  xAxisLabel?: string
  yAxisLabel?: string
  animate?: boolean
  hideCategoryLabelsAfter?: number
}

type TooltipData = {
  value: number
  category: string
  color: string
  x: number
  y: number
}

export function ColumnChart({
  width = 500,
  height = 400,
  margin = defaultMargin,
  chartData,
  backgroundColor = '#FFF',
  tooltipUnit = '',
  xAxisLabel = 'Categories',
  yAxisLabel = 'Values',
  animate = true,
  hideCategoryLabelsAfter = 10,
}: ColumnChartProps) {
  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null)
  const [isMouseInChart, setIsMouseInChart] = useState(false)
  const [hoveredBar, setHoveredBar] = useState<string | null>(null)

  if (width < 10) return null

  const { data, color } = chartData

  // Bounds
  const innerWidth = width - margin.left - margin.right
  const innerHeight = height - margin.top - margin.bottom

  // Scales
  const xScale = scaleBand<string>({
    domain: data.map((d) => d.category),
    padding: 0.2,
    range: [0, innerWidth],
  })

  const yScale = scaleLinear<number>({
    domain: [0, Math.max(...data.map((d) => d.value))],
    range: [innerHeight, 0],
    nice: true,
  })

  // Add this check near your other scale definitions
  const showCategoryLabels = data.length <= hideCategoryLabelsAfter

  // Get formatted data for display
  const getFormattedValue = (value: number) => {
    return new Intl.NumberFormat().format(value)
  }

  // Create animated springs for all bars
  const animatedBars = useSprings(
    data.length,
    data.map((d, i) => {
      const barHeight = innerHeight - (yScale(d.value) ?? 0)
      const barY = innerHeight - barHeight
      return {
        from: animate ? { height: 0, y: innerHeight } : { height: barHeight, y: barY },
        to: { height: barHeight, y: barY },
        delay: animate ? i * 20 : 0,
        config: { tension: 300, friction: 20 },
      }
    })
  )

  return (
    <>
      <div
        className="relative"
        onMouseEnter={() => setIsMouseInChart(true)}
        onMouseLeave={() => {
          setIsMouseInChart(false)
          setHoveredBar(null)
          setTooltipData(null)
        }}
      >
        <svg width={width} height={height}>
          <rect rx={14} width={innerWidth} height={innerHeight} fill={backgroundColor} />
          <Group top={margin.top} left={margin.left}>
            {/* Y-axis label */}
            <text
              x={-innerHeight / 2}
              y={-35}
              transform="rotate(-90)"
              textAnchor="middle"
              className="text-secondary"
              fontSize={12}
            >
              {yAxisLabel}
            </text>

            <GridRows
              scale={yScale}
              width={innerWidth}
              height={innerHeight}
              stroke="#e0e0e0"
              strokeDasharray="2,2"
              strokeOpacity={0.6}
            />

            {data.map((d, i) => {
              const barWidth = xScale.bandwidth()
              const barX = xScale(d.category) ?? 0

              const isHovered = hoveredBar === d.category
              const barOpacity = isMouseInChart ? (isHovered ? 1 : 0.2) : 1

              const AnimatedBar = animated(Bar)

              return (
                <AnimatedBar
                  key={`bar-${i}`}
                  x={barX}
                  y={animatedBars[i].y}
                  width={barWidth}
                  height={animatedBars[i].height}
                  fill={color}
                  stroke="white"
                  strokeWidth={3}
                  rx={4}
                  ry={4}
                  opacity={barOpacity}
                  className="transition-opacity duration-200"
                  onMouseMove={(event) => {
                    const point = localPoint(event) || { x: 0, y: 0 }
                    setHoveredBar(d.category)
                    setTooltipData({
                      value: d.value,
                      category: d.category,
                      color: color,
                      x: point.x,
                      y: point.y,
                    })
                  }}
                  onMouseLeave={() => {
                    setTooltipData(null)
                    // Don't reset hoveredBar here to maintain hover state when moving between bars
                  }}
                />
              )
            })}

            {data.map((d, i) => {
              const barWidth = xScale.bandwidth()
              const barHeight = innerHeight - (yScale(d.value) ?? 0)
              const barX = xScale(d.category) ?? 0
              const barY = innerHeight - barHeight

              if (barWidth < 30) return null

              const isHovered = hoveredBar === d.category
              const textOpacity = isMouseInChart ? (isHovered ? 1 : 0.2) : 1

              return (
                <text
                  key={`bar-label-${i}`}
                  x={barX + barWidth / 2}
                  y={barY - 14}
                  fontSize={14}
                  fontWeight="normal"
                  className="text-primary"
                  textAnchor="middle"
                  opacity={textOpacity}
                  style={{ transition: 'opacity 200ms' }}
                >
                  {`${getFormattedValue(d.value)}${tooltipUnit ? ` ${tooltipUnit}` : ''}`}
                </text>
              )
            })}

            <AxisLeft
              scale={yScale}
              left={10}
              stroke="#888888"
              tickStroke="#888888"
              numTicks={6}
              tickLabelProps={{
                fontSize: 12,
                className: 'text-secondary',
                textAnchor: 'end',
                dy: '0.33em',
              }}
            />
            <AxisBottom
              top={innerHeight}
              scale={xScale}
              stroke="#888888"
              tickStroke="#888888"
              tickLabelProps={() => ({
                fontSize: 12,
                className: 'text-secondary',
                textAnchor: 'middle',
                dy: '0.33em',
                // Hide the text if we have too many categories
                opacity: showCategoryLabels ? 1 : 0,
              })}
            />

            {/* X-axis label */}
            <text x={innerWidth / 2} y={innerHeight + 45} textAnchor="middle" fontSize={12} className="text-primary">
              {xAxisLabel}
            </text>
          </Group>
        </svg>
        {tooltipData && (
          <div
            className="absolute bg-white text-primary shadow-md rounded p-2 pointer-events-none z-10 text-sm"
            style={{
              top: tooltipData.y - 10,
              left: tooltipData.x,
              transform: 'translate(-50%, -100%)',
              border: '1px solid bg-primary',
              whiteSpace: 'nowrap',
            }}
          >
            <div className="text-primary mb-1">{tooltipData.category}</div>
            <div className="flex items-center gap-2 text-secondary">
              <div
                className="w-4 h-4 shrink-0 rounded-sm"
                style={{
                  backgroundColor: tooltipData.color,
                }}
              />
              <span>{`${getFormattedValue(tooltipData.value)}${tooltipUnit ? ` ${tooltipUnit}` : ''}`}</span>
            </div>
            {/* Arrow pointing down */}
            <div
              className="absolute w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-white"
              style={{
                bottom: '-6px',
                left: '50%',
                marginLeft: '-6px',
                borderColor: 'bg-primary',
              }}
            ></div>
          </div>
        )}
      </div>
    </>
  )
}
